import React, { useCallback, useEffect, useRef, useState } from "react";
import type { HttpAdapter } from "./createTransport";
import { ensureStyles } from "./styles";

interface IssuedCode {
  code: string;
  expiresAt: string;
}

export interface PhoneSupportCodeProps {
  http: HttpAdapter;
  /** Shown so the caller knows where to dial. */
  phoneNumber?: string;
  className?: string;
}

/** Grouped as 3+3 — far easier to read aloud than six run-together digits. */
const formatCode = (code: string): string =>
  code.length === 6 ? `${code.slice(0, 3)} ${code.slice(3)}` : code;

const mmss = (ms: number): string => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

/**
 * Issues a short-lived code that authenticates the user on a phone call.
 *
 * Why this exists: a phone call carries no JWT, and caller ID is both
 * spoofable and unreliable (people call from work phones, partners' phones,
 * hotels). A code minted inside an authenticated session proves the caller
 * holds that session — the same guarantee chat relies on — and works from
 * any handset.
 *
 * The code is deliberately NOT persisted anywhere client-side: it lives in
 * component state, disappears on unmount, and expires server-side regardless.
 */
export const PhoneSupportCode: React.FC<PhoneSupportCodeProps> = ({
  http,
  phoneNumber,
  className,
}) => {
  const [issued, setIssued] = useState<IssuedCode | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    ensureStyles();
    return () => {
      mounted.current = false;
    };
  }, []);

  const request = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await http.post<IssuedCode>("/support/voice-code");
      if (!mounted.current) return;
      setIssued(res);
    } catch (err: any) {
      if (!mounted.current) return;
      // 429 is the throttle, and it is the one case worth wording precisely —
      // otherwise people retry immediately and dig themselves deeper.
      const status = err?.response?.status ?? err?.status;
      setError(
        status === 429
          ? "You've requested several codes just now. Please wait a few minutes and try again."
          : "We couldn't generate a code. Please try again.",
      );
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [http]);

  // Countdown. Ticking locally keeps the display honest without polling the
  // server; expiry is enforced server-side either way.
  useEffect(() => {
    if (!issued) return;
    const target = new Date(issued.expiresAt).getTime();
    const tick = () => {
      const left = target - Date.now();
      setRemaining(left);
      if (left <= 0) setIssued(null);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [issued]);

  return (
    // `lh-support-base`, NOT `lh-support`: the latter carries position:fixed
    // for the floating chat launcher, which would tear this card out of
    // whatever dashboard it is embedded in.
    <div className={`lh-support-base lh-voice-code ${className ?? ""}`}>
      {!issued ? (
        <>
          <p className="lh-voice-code__intro">
            Calling us? Generate a code to confirm it's you — you'll be asked
            to read it out.
          </p>
          <button
            type="button"
            className="lh-voice-code__btn"
            onClick={() => void request()}
            disabled={loading}
          >
            {loading ? "Generating…" : "Get a code for phone support"}
          </button>
        </>
      ) : (
        <>
          <p className="lh-voice-code__intro">
            {phoneNumber ? (
              <>
                Call <strong>{phoneNumber}</strong> and read this code when
                asked:
              </>
            ) : (
              <>Read this code when asked:</>
            )}
          </p>
          {/* aria-label spells the digits so a screen reader doesn't read
              "418302" as a single number. */}
          <div
            className="lh-voice-code__code"
            aria-label={issued.code.split("").join(" ")}
          >
            {formatCode(issued.code)}
          </div>
          <p className="lh-voice-code__meta">
            Expires in {mmss(remaining)} · single use
          </p>
          <button
            type="button"
            className="lh-voice-code__link"
            onClick={() => void request()}
            disabled={loading}
          >
            {loading ? "Generating…" : "Get a new code"}
          </button>
        </>
      )}

      {error && <p className="lh-voice-code__error">{error}</p>}
    </div>
  );
};

export default PhoneSupportCode;
